import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, Zap, Wallet, Infinity as InfinityIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CreditsPage() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/credits").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buy = async (pack) => {
    setBusy(pack);
    try {
      const { data: res } = await api.post("/credits/buy", { pack });
      toast.success(`Credits added! You now have ${res.credits} AI credits ⚡`);
      await load(); await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purchase failed");
    } finally { setBusy(null); }
  };

  if (!data) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 md:px-10" data-testid="credits-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI Credits</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
        Keep the AI Scanner <span className="text-accent">working for you</span>
      </h1>
      <p className="mt-3 max-w-xl text-sm text-muted-foreground">
        Every scan itemises receipts, reviews CVs and turns documents into plans. You get {data.free_limit} free scans — after that, each scan uses 1 credit.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <ScanLine className="mx-auto h-5 w-5 text-accent" />
          <div className="mt-2 font-display text-2xl" data-testid="credits-free-used">{data.free_used} / {data.free_limit}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Free scans used</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          {data.unlimited ? <InfinityIcon className="mx-auto h-5 w-5 text-emerald-500" /> : <Zap className="mx-auto h-5 w-5 text-primary" />}
          <div className="mt-2 font-display text-2xl" data-testid="credits-balance">{data.unlimited ? "∞" : data.credits}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{data.unlimited ? "Unlimited (Pro)" : "AI credits"}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <Wallet className="mx-auto h-5 w-5 text-primary" />
          <div className="mt-2 font-display text-2xl" data-testid="credits-wallet">£{Number(data.wallet).toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Wallet balance</div>
        </div>
      </div>

      {!data.unlimited && (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {data.packs.map((p) => (
            <div key={p.id} className={`rounded-2xl border p-6 ${p.id === "pack_50" ? "border-accent/50 bg-accent/5" : "border-border bg-card"}`} data-testid={`credit-pack-${p.id}`}>
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-xl">{p.label}</h3>
                {p.id === "pack_50" && <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">BEST VALUE</span>}
              </div>
              <div className="mt-2 font-display text-3xl">£{p.price.toFixed(2)}</div>
              <p className="mt-1 text-xs text-muted-foreground">≈ £{(p.price / p.credits).toFixed(2)} per scan · paid from your wallet</p>
              <Button onClick={() => buy(p.id)} disabled={!!busy} className="mt-4 w-full" data-testid={`buy-${p.id}`}>
                {busy === p.id ? "Processing…" : `Buy ${p.credits} credits`}
              </Button>
            </div>
          ))}
        </div>
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        Wallet funds are spend-only and clear to app revenue after 2 days — <Link to="/pricing" className="text-primary underline">go unlimited with Zuntria Pro</Link> instead.
      </p>
    </div>
  );
}
